import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import type { PartnerRepository } from './repository';
import type { PartnerDetail } from './types';

interface PartnerFormValues {
  readonly code: string;
  readonly name: string;
  readonly affiliateCommissionExpiryDays: number | null;
  readonly defaultCurrencyCode: string;
  readonly timezone: string;
  readonly usernameNormalizationMode: 'LOWERCASE_NFKC' | 'UPPERCASE_NFKC' | 'EXACT_NFKC';
  readonly requestRateLimitPerMinute: number;
  readonly withdrawalRateLimitPerMinute: number;
  readonly timestampToleranceSeconds: number;
  readonly reason: string;
}

export interface PartnerFormDialogProps {
  readonly onClose: () => void;
  readonly onSaved: (partner: PartnerDetail) => void;
  readonly open: boolean;
  readonly partner?: PartnerDetail | undefined;
  readonly repository: PartnerRepository;
}

export function PartnerFormDialog({
  onClose,
  onSaved,
  open,
  partner,
  repository,
}: PartnerFormDialogProps) {
  const { t } = useTranslation();
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<PartnerFormValues>({ defaultValues: defaults(partner) });

  useEffect(() => {
    if (!open) return;
    reset(defaults(partner));
    setError(null);
    submittingRef.current = false;
    setSubmitting(false);
  }, [open, partner, reset]);

  const submit = handleSubmit(async (values) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const response =
        partner === undefined
          ? await repository.create({
              code: values.code.trim(),
              name: values.name.trim(),
              affiliateCommissionExpiryDays: values.affiliateCommissionExpiryDays,
              defaultCurrencyCode: values.defaultCurrencyCode.trim(),
              timezone: values.timezone.trim(),
              usernameNormalizationMode: values.usernameNormalizationMode,
              requestRateLimitPerMinute: Number(values.requestRateLimitPerMinute),
              withdrawalRateLimitPerMinute: Number(values.withdrawalRateLimitPerMinute),
              timestampToleranceSeconds: Number(values.timestampToleranceSeconds),
              reason: values.reason.trim(),
            })
          : await repository.update(partner.id, {
              name: values.name.trim(),
              affiliateCommissionExpiryDays: values.affiliateCommissionExpiryDays,
              defaultCurrencyCode: values.defaultCurrencyCode.trim(),
              timezone: values.timezone.trim(),
              usernameNormalizationMode: values.usernameNormalizationMode,
              requestRateLimitPerMinute: Number(values.requestRateLimitPerMinute),
              withdrawalRateLimitPerMinute: Number(values.withdrawalRateLimitPerMinute),
              timestampToleranceSeconds: Number(values.timestampToleranceSeconds),
              reason: values.reason.trim(),
            });
      onSaved(response.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('partners.errors.mutation')));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  });

  const close = () => {
    if (!submittingRef.current) onClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={close} open={open}>
      <DialogTitle>
        {t(partner === undefined ? 'partners.form.createTitle' : 'partners.form.editTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="partner-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <TextField
            disabled={submitting || partner !== undefined}
            error={errors.code !== undefined}
            helperText={errors.code?.message}
            label={t('partners.fields.code')}
            {...register('code', {
              required: t('partners.validation.codeRequired'),
              pattern: {
                value: /^[A-Za-z0-9][A-Za-z0-9_-]{1,31}$/u,
                message: t('partners.validation.codeInvalid'),
              },
            })}
          />
          <TextField
            autoFocus={partner !== undefined}
            disabled={submitting}
            error={errors.name !== undefined}
            helperText={errors.name?.message}
            label={t('partners.fields.name')}
            {...register('name', {
              required: t('partners.validation.nameRequired'),
              validate: (value) => value.trim().length > 0 || t('partners.validation.nameRequired'),
            })}
          />
          <TextField
            disabled={submitting}
            error={errors.affiliateCommissionExpiryDays !== undefined}
            helperText={
              errors.affiliateCommissionExpiryDays?.message ??
              t('partners.form.affiliateCommissionExpiryHelp')
            }
            label={t('partners.fields.affiliateCommissionExpiry')}
            slotProps={{ htmlInput: { min: 1, max: 365 } }}
            type="number"
            {...register('affiliateCommissionExpiryDays', {
              setValueAs: (value: unknown) => (value === '' ? null : Number(value)),
              validate: (value) =>
                value === null ||
                (Number.isInteger(value) && value >= 1 && value <= 365) ||
                t('partners.validation.expiryRange'),
            })}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              disabled={submitting}
              error={errors.defaultCurrencyCode !== undefined}
              helperText={errors.defaultCurrencyCode?.message}
              label={t('partners.fields.currency')}
              {...register('defaultCurrencyCode', {
                required: t('partners.validation.currencyRequired'),
                pattern: {
                  value: /^[A-Za-z]{3}$/u,
                  message: t('partners.validation.currencyInvalid'),
                },
              })}
            />
            <TextField
              disabled={submitting}
              error={errors.timezone !== undefined}
              helperText={errors.timezone?.message}
              label={t('partners.fields.timezone')}
              {...register('timezone', { required: t('partners.validation.timezoneRequired') })}
            />
          </Stack>
          <TextField
            defaultValue={partner?.usernameNormalizationMode ?? 'LOWERCASE_NFKC'}
            disabled={submitting}
            label={t('partners.fields.usernameNormalization')}
            select
            {...register('usernameNormalizationMode')}
          >
            <MenuItem value="LOWERCASE_NFKC">LOWERCASE_NFKC</MenuItem>
            <MenuItem value="UPPERCASE_NFKC">UPPERCASE_NFKC</MenuItem>
            <MenuItem value="EXACT_NFKC">EXACT_NFKC</MenuItem>
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <NumberField
              disabled={submitting}
              error={errors.requestRateLimitPerMinute?.message}
              label={t('partners.fields.requestRateLimit')}
              registration={register('requestRateLimitPerMinute', {
                min: { value: 1, message: t('partners.validation.positive') },
                valueAsNumber: true,
              })}
            />
            <NumberField
              disabled={submitting}
              error={errors.withdrawalRateLimitPerMinute?.message}
              label={t('partners.fields.withdrawalRateLimit')}
              registration={register('withdrawalRateLimitPerMinute', {
                min: { value: 1, message: t('partners.validation.positive') },
                valueAsNumber: true,
              })}
            />
            <NumberField
              disabled={submitting}
              error={errors.timestampToleranceSeconds?.message}
              label={t('partners.fields.timestampTolerance')}
              registration={register('timestampToleranceSeconds', {
                min: { value: 0, message: t('partners.validation.nonNegative') },
                max: { value: 3600, message: t('partners.validation.toleranceMaximum') },
                valueAsNumber: true,
              })}
            />
          </Stack>
          <TextField
            disabled={submitting}
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('partners.fields.reason')}
            multiline
            minRows={2}
            {...register('reason', {
              required: t('partners.validation.reasonRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('partners.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="partner-form" loading={submitting} type="submit" variant="contained">
          {t(partner === undefined ? 'actions.create' : 'actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function NumberField({
  disabled,
  error,
  label,
  registration,
}: {
  readonly disabled: boolean;
  readonly error?: string | undefined;
  readonly label: string;
  readonly registration: UseFormRegisterReturn;
}) {
  return (
    <TextField
      disabled={disabled}
      error={error !== undefined}
      helperText={error}
      label={label}
      slotProps={{ htmlInput: { min: 0 } }}
      type="number"
      {...registration}
    />
  );
}

function defaults(partner: PartnerDetail | undefined): PartnerFormValues {
  return {
    code: partner?.code ?? '',
    name: partner?.name ?? '',
    affiliateCommissionExpiryDays: partner?.affiliateCommissionExpiryDays ?? 30,
    defaultCurrencyCode: partner?.defaultCurrencyCode ?? 'THB',
    timezone: partner?.timezone ?? 'Asia/Bangkok',
    usernameNormalizationMode: partner?.usernameNormalizationMode ?? 'LOWERCASE_NFKC',
    requestRateLimitPerMinute: partner?.requestRateLimitPerMinute ?? 100,
    withdrawalRateLimitPerMinute: partner?.withdrawalRateLimitPerMinute ?? 10,
    timestampToleranceSeconds: partner?.timestampToleranceSeconds ?? 300,
    reason: '',
  };
}

export function toDialogError(
  error: unknown,
  fallback: string,
): Pick<AppError, 'message' | 'requestId'> {
  if (typeof error === 'object' && error !== null && 'message' in error && 'requestId' in error) {
    const candidate = error as { readonly message: unknown; readonly requestId: unknown };
    return {
      message: typeof candidate.message === 'string' ? candidate.message : fallback,
      requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
    };
  }
  return { message: fallback, requestId: null };
}

export function MutationError({
  error,
}: {
  readonly error: Pick<AppError, 'message' | 'requestId'>;
}) {
  return (
    <Alert severity="error">
      {error.message}
      {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
    </Alert>
  );
}
